/**
 * @file Case d'inscription à la newsletter de l'établissement, partagée par les parcours publics.
 * @module packages.common.components.newsletter-consent.NewsletterConsentBox
 *
 * Même case et même texte dans le widget de réservation restaurant et à l'achat
 * d'un bon cadeau. Le composant est contrôlé et ne dépend d'aucun formulaire :
 * l'achat de bon cadeau le branche sur React Hook Form, le widget sur son état
 * de parcours. Côté serveur, une case cochée inscrit le client et une case
 * décochée ne le désinscrit jamais.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {Checkbox, FormControlLabel, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

// ----------------------------------------------------------------------

/**
 * Case « recevoir les offres par email ».
 *
 * @param {Object} props
 * @param {boolean} props.checked - État de la case
 * @param {Function} props.onChange - Appelé avec la nouvelle valeur booléenne
 * @param {('small'|'medium')} [props.size='medium'] - Taille de la case et du texte :
 *   `small` s'aligne sur les cases compactes du widget de réservation
 * @param {Object} [props.sx] - Styles additionnels du libellé
 * @return {JSX.Element} Case à cocher avec son libellé traduit
 */
export default function NewsletterConsentBox({checked, onChange, size = 'medium', sx}) {
  const {t} = useTranslation();
  const isSmall = size === 'small';

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          // Taille du thème par défaut, pour rester alignée sur les autres cases du formulaire
          size={isSmall ? 'small' : undefined}
          sx={isSmall ? {p: '2px', mt: '1px', mr: '4px', flexShrink: 0} : undefined}
        />
      }
      // Taille normale : typographie des libellés du thème, comme les autres cases du formulaire
      label={
        isSmall ? (
          <Typography variant="body2" sx={{lineHeight: 1.5}}>
            {t('common.newsletterConsent')}
          </Typography>
        ) : (
          t('common.newsletterConsent')
        )
      }
      sx={[isSmall && {alignItems: 'flex-start', ml: 0, mr: 0}, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    />
  );
}

NewsletterConsentBox.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['small', 'medium']),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};
